import React, { useCallback, useContext, useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import { NavLink, useHistory } from "react-router-dom";

import { faEdit, faUserEdit, faUserTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Popup } from "semantic-ui-react";

import AppContext from "~/common/AppContext";
import PageHeader from "~/common/PageHeader";
import { confirm, prompt } from "~/lib/prompts";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { getStreamLastOpenedKey, streamStore } from "~/stream/utils";
import TeamMembersModal from "~/team/components/TeamMembersModal";
import CreateOrEditTeam, { editTeamMutation, TeamEditType } from "~/team/CreateOrEditTeam";
import { toast, useModals } from "~/ui";
import { DropdownItem } from "~/ui/ArrowDropdown";

import type { CreateOrEditTeamEditMutation } from "~/__generated__/CreateOrEditTeamEditMutation.graphql";
import type { TeamHeaderLeaveTeamMutation } from "~/__generated__/TeamHeaderLeaveTeamMutation.graphql";
import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import stylesOldStreamHeader from "~/stream/css/OldStreamHeader.module.css";
import stylesUsersIcon from "~/stream/css/OldUsersIcon.module.css";

import styles from "./TeamHeader.module.css";

export const SCROLL_THRESHOLD = 40;

interface TeamHeaderProps {
	team: Omit<Team, " $fragmentType">;
	isGoogleWorkspace: boolean;
	isOffice365Workspace: boolean;
}

const leaveTeamMutation = graphql`
	mutation TeamHeaderLeaveTeamMutation($id: ID!) {
		leaveTeam(id: $id) {
			team {
				id
				title
				description
				members {
					id
				}
				parent {
					id
				}
				handle
				location
				archived
				hasAccess
			}
		}
	}
`;

const emptyArray: React.ReactNode[] = [];

export default function TeamHeader({ team, isGoogleWorkspace, isOffice365Workspace }: TeamHeaderProps) {
	const { user } = useContext(AppContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const modals = useModals();
	const history = useHistory();
	const baseUrl = `/teams/${team.id}/`;

	const onRenameTeam = useCallback(() => {
		const { id, title } = team;
		if (!id) {
			return;
		}

		prompt("", { initialInputValue: title, title: "Rename team?" }).then(newTitle => {
			if (!newTitle || typeof newTitle !== "string" || newTitle == title) {
				return;
			}

			commitMutation<CreateOrEditTeamEditMutation>(environment, {
				mutation: editTeamMutation,
				variables: {
					id,
					title: newTitle,
				},
				onCompleted: () => {
					toast.success("Team renamed.");
				},
				onError: () => {
					toast.error("Unable to update team title");
				},
			});
		});
	}, [team]);

	const onEditTeamDetails = useCallback(() => {
		modals.open(({ close }) => <CreateOrEditTeam team={team} editType={TeamEditType.DETAILS} onClose={close} />);
	}, [team, modals]);

	const onEditTeamMembers = useCallback(() => {
		modals.open(({ close }) => <CreateOrEditTeam team={team} editType={TeamEditType.MEMBERS} onClose={close} />);
	}, [team, modals]);

	const onClickLeaveTeam = useCallback(async () => {
		if (
			await confirm(<p>Leaving a team will make you lose access to content that belongs to this team.</p>, {
				yesText: "Leave Team",
				yesProps: { variant: "destructive" },
				title: "Are you sure?",
				simple: true,
			})
		) {
			setIsLoading(true);
			commitMutationPromise<TeamHeaderLeaveTeamMutation>(environment, {
				mutation: leaveTeamMutation,
				variables: {
					id: team.id,
				},
				updater: store => {
					const viewer = store.get("viewer");
					const myTeams = viewer?.getLinkedRecords("myTeams");
					if (!myTeams) {
						return;
					}
					const teamRecord = store.get(team.id);
					if (!teamRecord) {
						return;
					}
					const index = myTeams.findIndex(team => team.getDataID() === teamRecord.getDataID());
					if (index < 0) {
						return;
					}
					myTeams.splice(index, 1);
					viewer?.setLinkedRecords(myTeams, "myTeams");
				},
				onCompleted: () => {
					setIsLoading(false);
					if (user) streamStore.remove(getStreamLastOpenedKey(user.id));
					history.push("/stream/");
				},
			});
		}
	}, [team, setIsLoading, user, history]);

	const onClickMembers = useCallback(() => {
		modals.open(({ close }) => (
			<TeamMembersModal
				onClose={close}
				onClickEditTeamMembers={onEditTeamMembers}
				teamTitle={team.title}
				members={team.members}
				sync={team.sync}
				hasEditAccess={team.hasAccess}
				isGoogleWorkspace={isGoogleWorkspace}
			/>
		));
	}, [modals, onEditTeamMembers, team, isGoogleWorkspace]);

	const subject = useMemo(
		() => (
			<div className={stylesOldStreamHeader.subject}>
				{team.hasAccess && !team.isAccountWideTeam ? (
					<Popup
						inverted
						size="tiny"
						content="Rename team"
						position="bottom center"
						trigger={
							<div className={styles.renameModalTrigger} onClick={onRenameTeam}>
								<h4
									className={cx(styles.title, { [styles.untitled]: !team.title })}
									data-test-id="team-header-team-name"
								>
									{team?.title || "Untitled team"}
								</h4>
								<FontAwesomeIcon icon={faEdit} className={styles.editIcon} />
							</div>
						}
						className={stylesOldStreamHeader.popup}
					/>
				) : (
					<h4 className={styles.title} data-test-id="team-header-team-name">
						{team?.title || "Untitled team"}
					</h4>
				)}
			</div>
		),
		[team, onRenameTeam],
	);

	const isSynced = team.sync !== null;
	const syncedTooltipContent = useMemo(() => {
		if (team.sync?.fromManager) {
			return `Synced with ${team.sync?.fromManager?.fullName} and their ${team.members.length - 1} direct report`;
		} else if (team.sync?.fromExternalGroupName) {
			if (isGoogleWorkspace) {
				return `Synced with ${team.sync.fromExternalGroupName} Google Workspace group`;
			} else if (isOffice365Workspace) {
				return `Synced with ${team.sync.fromExternalGroupName} Microsoft 365 group`;
			} else {
				return "";
			}
		} else {
			return `This team has ${team.members.length} members. Add more teammates to collaborate.`;
		}
	}, [team.sync, team.members.length, isGoogleWorkspace, isOffice365Workspace]);

	return (
		<div className={stylesOldStreamHeader.container}>
			<PageHeader
				className={stylesOldStreamHeader.pageHeader}
				subject={subject}
				actions={emptyArray}
				mobileShowActionButton
				moreActions={[
					<DropdownItem
						key="edit-team-details"
						onClick={onEditTeamDetails}
						icon={<DropdownItem.Icon icon={faEdit} />}
						disabled={!team.hasAccess || team.isAccountWideTeam}
						disabledHelpText={
							team.isAccountWideTeam
								? "The organization-wide team cannot be modified"
								: "Only members can perform this action"
						}
					>
						Edit team details
					</DropdownItem>,
					<DropdownItem
						key="edit-team-members"
						onClick={onEditTeamMembers}
						icon={<DropdownItem.Icon icon={faUserEdit} />}
						disabled={!team.hasAccess || team.isAccountWideTeam}
						disabledHelpText={
							team.isAccountWideTeam
								? "The organization-wide team cannot be modified"
								: "Only members can perform this action"
						}
					>
						Edit team members
					</DropdownItem>,
					<hr className={styles.dropdownDivider} key="top-divider" />,
					<DropdownItem
						key="leave-team"
						className={styles.destructiveAction}
						onClick={onClickLeaveTeam}
						icon={<DropdownItem.Icon icon={faUserTimes} />}
						disabled={!team.hasAccess || team.isAccountWideTeam || isLoading}
						disabledHelpText={
							team.isAccountWideTeam
								? "The organization-wide team cannot be modified"
								: "Only members can perform this action"
						}
					>
						Leave team
					</DropdownItem>,
				]}
			/>
			<div className={stylesOldStreamHeader.iconsContainer}>
				{!team.isAccountWideTeam && (
					<Popup
						inverted
						size="tiny"
						content={syncedTooltipContent}
						position="bottom center"
						trigger={
							<div className={cx(stylesUsersIcon.container, stylesUsersIcon.clickable)} onClick={onClickMembers}>
								<FontAwesomeIcon icon={faUsers} />
								<p>
									{team.members.length}&nbsp;
									{isSynced && <span className={styles.synced}>Synced</span>}
								</p>
							</div>
						}
						className={stylesOldStreamHeader.popup}
					/>
				)}

				{team.handle && (
					<p title={team.handle} className={styles.handle}>
						@{team.handle}
					</p>
				)}
				<p title={team.description || ""} className={styles.description}>
					{team.description}
				</p>
			</div>
			<div className={stylesOldStreamHeader.separator} />
			<div className={stylesOldStreamHeader.tabs}>
				<nav>
					{/* For now we only have one tab but we will have more in the future */}
					<NavLink
						to={baseUrl}
						className={stylesOldStreamHeader.tabContainer}
						activeClassName={stylesOldStreamHeader.tabContainerActive}
					>
						<span className={stylesOldStreamHeader.tabUnderline}>Objectives</span>
					</NavLink>
				</nav>
			</div>
		</div>
	);
}
